import {
  ClipboardCheck,
  CircleUser,
  FileText,
  Handshake,
  LayoutDashboard,
  LogOut,
  Mic,
  Notebook,
  Settings,
  Sparkles,
  Upload,
  Users,
  Building2,
  ChevronUp,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  CanAccess,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useLogout,
  useTranslate,
} from "ra-core";
import { Link, useLocation } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SellerAvatar } from "../meetings/SellerAvatar";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";

/**
 * SidebarMusco — sidebar permanente 230px del layout P3 Bento Spatial.
 *
 * Estructura (top→bottom):
 *   1. Logo + nombre de la app
 *   2. Sección "Workspace" con navegación principal:
 *      Dashboard · Empresas · Contactos · Oportunidades · Juntas · Tareas · Notas
 *   3. Card "Pipeline" con conteos por stage de deal
 *   4. (margin-top auto) Avatar del usuario + dropdown con acciones
 *
 * Las cuentas (count) se calculan via useGetList(resource, { pagination: { perPage: 0 } })
 * que devuelve total sin traer payload pesado.
 */
export function SidebarMusco() {
  const { title, dealStages, dealPipelineStatuses } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  // Conteos para nav badges (perPage=1 mínimo; total viene en meta).
  // "Juntas" muestra solo las del usuario logueado — coincide con el filtro
  // que aplica el dashboard /meetings (Mis juntas).
  const { total: meetingsTotal } = useGetList(
    "meetings_summary",
    {
      pagination: { page: 1, perPage: 1 },
      filter: identity?.id
        ? { "sales_participant_ids@cs": `{${identity.id}}` }
        : undefined,
    },
    { enabled: !!identity?.id },
  );
  const { total: dealsTotal } = useGetList("deals", {
    pagination: { page: 1, perPage: 1 },
    filter: { "stage@in": `(${dealPipelineStatuses.map((s) => `"${s}"`).join(",")})` },
  });
  const { total: tasksTotal } = useGetList("tasks", {
    pagination: { page: 1, perPage: 1 },
    // PostgREST: para IS NULL hay que usar el operador `is`, no `eq`.
    // Sin @is el adapter genera `done_date=eq.null` que rechaza con 400.
    filter: { "done_date@is": null },
  });

  // Pipeline summary — conteo por stage (sólo los del pipeline activo)
  const { data: pipelineRows } = useGetList("deals", {
    pagination: { page: 1, perPage: 200 },
    filter: { "stage@in": `(${dealPipelineStatuses.map((s) => `"${s}"`).join(",")})` },
  });
  const pipelineByStage = (pipelineRows ?? []).reduce<Record<string, number>>(
    (acc, d: any) => {
      acc[d.stage] = (acc[d.stage] ?? 0) + 1;
      return acc;
    },
    {},
  );

  const items: Array<{
    to: string;
    icon: React.ComponentType<{ size?: number }>;
    label: string;
    count?: number;
    match?: (path: string) => boolean;
  }> = [
    {
      to: "/",
      icon: LayoutDashboard,
      label: translate("ra.page.dashboard"),
      match: (p) => p === "/" || p === "",
    },
    {
      to: "/companies",
      icon: Building2,
      label: translate("resources.companies.name", { smart_count: 2 }),
      match: (p) => p.startsWith("/companies"),
    },
    {
      to: "/contacts",
      icon: Users,
      label: translate("resources.contacts.name", { smart_count: 2 }),
      match: (p) => p.startsWith("/contacts"),
    },
    {
      to: "/deals",
      icon: Handshake,
      label: translate("resources.deals.name", { smart_count: 2 }),
      count: dealsTotal,
      match: (p) => p.startsWith("/deals"),
    },
    {
      to: "/meetings",
      icon: Mic,
      label: "Juntas",
      count: meetingsTotal,
      match: (p) => p.startsWith("/meetings"),
    },
    {
      to: "/tasks",
      icon: ClipboardCheck,
      label: translate("resources.tasks.name", { smart_count: 2 }),
      count: tasksTotal,
      match: (p) => p.startsWith("/tasks"),
    },
    {
      to: "/notes",
      icon: Notebook,
      label: translate("resources.notes.name", { smart_count: 2 }),
      match: (p) => p.startsWith("/notes"),
    },
  ];

  return (
    <aside
      className="musco-sidebar flex flex-col gap-5 px-4 py-5"
      style={{ width: 230, minWidth: 230, height: "100vh", position: "sticky", top: 0 }}
    >
      <BrandHeader title={title} />

      <div>
        <div className="musco-nav-sec">Workspace</div>
        <nav className="flex flex-col gap-[3px]">
          {items.map((it) => {
            const isActive = it.match ? it.match(location.pathname) : location.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                className={"musco-nav-item" + (isActive ? " active" : "")}
              >
                <it.icon size={16} />
                <span className="truncate">{it.label}</span>
                {typeof it.count === "number" && it.count > 0 ? (
                  <span className="count tnums">{it.count}</span>
                ) : null}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Pipeline card */}
      <div
        className="rounded-[14px] px-3.5 py-3"
        style={{ background: "var(--musco-panel)", boxShadow: "var(--musco-shadow)" }}
      >
        <h4
          className="text-xs font-semibold mb-2"
          style={{ color: "var(--musco-muted)" }}
        >
          Pipeline
        </h4>
        {dealStages.slice(0, 4).map((s) => (
          <div key={s.value} className="flex items-center justify-between py-1 text-xs">
            <span style={{ color: "var(--musco-muted)" }} className="truncate">
              {s.label}
            </span>
            <span className="font-semibold tnums">{pipelineByStage[s.value] ?? 0}</span>
          </div>
        ))}
      </div>

      {/* User chip + dropdown */}
      <div className="mt-auto">
        <UserMenu />
      </div>
    </aside>
  );
}

function BrandHeader({ title }: { title: string }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 px-1.5 py-0.5">
      <div
        className="grid place-items-center font-extrabold text-base"
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: "var(--musco-accent)",
          color: "var(--musco-accent-foreground)",
          boxShadow: "var(--musco-shadow)",
        }}
      >
        {title.charAt(0).toUpperCase()}
      </div>
      <div className="font-bold text-base tracking-tight">{title}</div>
    </Link>
  );
}

function UserMenu() {
  const { identity, isLoading } = useGetIdentity();
  const logout = useLogout();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [sale, setSale] = useState<any>(null);

  // Cargar el `sale` (vendedor) actual para tener avatar + first_name
  useEffect(() => {
    if (!identity?.id) return;
    let cancelled = false;
    dataProvider
      .getOne("sales", { id: identity.id })
      .then(({ data }: any) => {
        if (!cancelled) setSale(data);
      })
      .catch(() => {
        /* identity might not match a sale (e.g. anonymous) — ok */
      });
    return () => {
      cancelled = true;
    };
  }, [identity?.id, dataProvider]);

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2.5 px-3 py-3"
        style={{
          background: "var(--musco-panel)",
          borderRadius: 12,
          boxShadow: "var(--musco-shadow)",
        }}
      />
    );
  }

  const displayName =
    sale?.first_name
      ? `${sale.first_name} ${sale.last_name ?? ""}`.trim()
      : identity?.fullName ?? "Usuario";
  const role = sale?.administrator ? "Administrador" : "Vendedor";

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2.5 px-3 py-2.5 w-full text-left transition-colors hover:[&]:opacity-95"
          style={{
            background: "var(--musco-panel)",
            borderRadius: 12,
            boxShadow: "var(--musco-shadow)",
          }}
        >
          <SellerAvatar
            id={identity?.id ?? "?"}
            name={displayName}
            size={32}
            imageUrl={sale?.avatar?.src}
          />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[13px] truncate">{displayName}</div>
            <div className="text-[11.5px]" style={{ color: "var(--musco-muted)" }}>
              {role}
            </div>
          </div>
          <ChevronUp size={14} style={{ color: "var(--musco-muted)" }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" sideOffset={8} className="w-56">
        <DropdownMenuItem asChild>
          <Link to={ProfilePage.path} className="flex items-center gap-2">
            <CircleUser size={16} />
            {translate("crm.profile.title")}
          </Link>
        </DropdownMenuItem>
        <CanAccess resource="sales" action="list">
          <DropdownMenuItem asChild>
            <Link to="/sales" className="flex items-center gap-2">
              <Users size={16} />
              {translate("resources.sales.name", { smart_count: 2 })}
            </Link>
          </DropdownMenuItem>
        </CanAccess>
        <CanAccess resource="configuration" action="edit">
          <DropdownMenuItem asChild>
            <Link to={SettingsPage.path} className="flex items-center gap-2">
              <Settings size={16} />
              {translate("crm.settings.title")}
            </Link>
          </DropdownMenuItem>
        </CanAccess>
        <DropdownMenuItem asChild>
          <Link to={ImportPage.path} className="flex items-center gap-2">
            <Upload size={16} />
            {translate("crm.header.import_data")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={ChangelogPage.path} className="flex items-center gap-2">
            <FileText size={16} />
            {translate("crm.changelog.title")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()} className="flex items-center gap-2">
          <LogOut size={16} />
          {translate("ra.auth.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* Tiny helper para que el icono Sparkles no se quede sin uso accidentalmente */
void Sparkles;
